import { isEqual } from "lodash";
import { useEffect, useLayoutEffect, useRef } from "react";
import type { DependencyList, EffectCallback } from "react";

import { useUpdate } from "../";

import { isDev } from "./helpers";

/**
 * @title useCustomCompareEffect
 */
export type UseCustomCompareEffect = <TDeps extends DependencyList>(
  /**
   * effect callback
   */
  effect: EffectCallback,
  /**
   * deps
   */
  deps: TDeps
) => void;

export const useCompareEffect: UseCustomCompareEffect = <TDeps extends DependencyList>(
  effect: EffectCallback,
  deps: TDeps
): void => {
  if (isDev) {
    if (!Array.isArray(deps) || !deps.length) {
      console.warn(
        "`useCustomCompareEffect` should not be used with no dependencies. Use React.useEffect instead."
      );
    }
  }

  const ref = useRef<TDeps | undefined>(undefined);
  const forceUpdate = useUpdate();

  if (!ref.current) {
    ref.current = deps;
  }

  useLayoutEffect(() => {
    if (!isEqual(deps, ref.current as TDeps)) {
      ref.current = deps;
      forceUpdate();
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, ref.current);
};
